// apps/client/src/boot/pwa.ts
export interface PwaWindow {
  isSecureContext: boolean;
  navigator: { serviceWorker?: { register(url: string): Promise<unknown> } };
  matchMedia(q: string): { matches: boolean };
  screen?: { orientation?: { lock?(o: string): Promise<void> } };
  addEventListener?(type: 'beforeinstallprompt' | 'appinstalled', fn: (e: Event) => void): void;
}
interface InstallPrompt extends Event { prompt(): Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> }
let deferred: InstallPrompt | null = null;
const listeners = new Set<() => void>();
const changed = () => { for (const fn of listeners) fn(); };

/** Service workers, install and push need a secure context (HTTPS or localhost); LAN http dev skips them silently (§8.10). */
export function registerPwa(win: PwaWindow = window as unknown as PwaWindow): 'registered' | 'skipped' {
  if (!win.isSecureContext || !win.navigator.serviceWorker) return 'skipped';
  win.navigator.serviceWorker.register('/sw.js').catch((e) => console.warn('sw registration failed', e));
  // Full screen (2026-10-15): the browser's own install prompt is kept for the title's tip and the settings entry (ui/install.tsx).
  win.addEventListener?.('beforeinstallprompt', (e) => { e.preventDefault(); deferred = e as InstallPrompt; changed(); });
  win.addEventListener?.('appinstalled', () => { deferred = null; changed(); });
  // The installed app (standalone, or full screen where the browser allows it, manifest display_override) holds landscape.
  if (win.matchMedia('(display-mode: standalone)').matches || win.matchMedia('(display-mode: fullscreen)').matches) {
    win.screen?.orientation?.lock?.('landscape').catch(() => {});
  }
  return 'registered';
}
export function canUsePush(): boolean {
  return typeof window !== 'undefined' && window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window;
}
/** Whether the browser gave us an install prompt that has not been used yet (Android, desktop Chrome; never iOS). */
export function canPromptInstall(): boolean {
  return deferred !== null;
}
/** Called whenever an install prompt arrives, is used, or the app is installed. Returns the unsubscribe. */
export function subscribeInstall(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
/** Shows the browser's install prompt (inside a tap); a prompt can be shown only once. True when the player installed. */
export async function promptInstall(): Promise<boolean> {
  const e = deferred;
  if (!e) return false;
  deferred = null;
  changed();
  await e.prompt();
  const { outcome } = await e.userChoice;
  return outcome === 'accepted';
}
